import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Chip,
  CircularProgress
} from '@mui/material'
import {
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Analytics as AnalyticsIcon,
  Launch as LaunchIcon,
  Dashboard as DashboardIcon,
  Link as LinkIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  TableChart as TableChartIcon,
  BarChart as BarChartIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Computer as ComputerIcon,
  PhoneAndroid as PhoneIcon,
  Web as WebIcon,
  Language as LanguageIcon,
  Public as PublicIcon
} from '@mui/icons-material'
import { useAuth } from '../../auth/useAuth'
import { useNavigate } from 'react-router-dom'

// Types
interface Application {
  id: string
  title: string
  description: string
  icon: React.ReactElement
  status: string
  requiredPermission: string
  path: string | null
  features: string[]
  showKeyFeatures?: boolean
  titleStyle?: React.CSSProperties
  descriptionStyle?: React.CSSProperties
}

interface LandingPageLink {
  Id: number
  Title: string
  Description: string | null
  Route: string
  RequiredGroup: string
  Visibility: string
  DisplayOrder: number
  Enabled: boolean
  Icon: string | null
  IsSystemLink: boolean
  KeyFeatures: string | null
  ShowKeyFeatures: boolean
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

// Enhanced icon mapping with all available icons
const iconMap: Record<string, React.ReactElement> = {
  // Basic icons
  LinkIcon: <LinkIcon />,
  TrendingUpIcon: <TrendingUpIcon />,
  AnalyticsIcon: <AnalyticsIcon />,
  SecurityIcon: <SecurityIcon />,
  DashboardIcon: <DashboardIcon />,
  AssessmentIcon: <AssessmentIcon />,
  
  // Business & Office icons
  BusinessIcon: <BusinessIcon />,
  WorkIcon: <WorkIcon />,
  DescriptionIcon: <DescriptionIcon />,
  TableChartIcon: <TableChartIcon />,
  BarChartIcon: <BarChartIcon />,
  EmailIcon: <EmailIcon />,
  CalendarIcon: <CalendarIcon />,
  
  // Technology icons
  StorageIcon: <StorageIcon />,
  CloudIcon: <CloudIcon />,
  ComputerIcon: <ComputerIcon />,
  PhoneIcon: <PhoneIcon />,
  WebIcon: <WebIcon />,
  LanguageIcon: <LanguageIcon />,
  PublicIcon: <PublicIcon />,
  LaunchIcon: <LaunchIcon />
}

const InternalLandingPage: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, userPermissions, logout, getAccessToken } = useAuth()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [landingPageLinks, setLandingPageLinks] = useState<LandingPageLink[]>([])
  const [loading, setLoading] = useState(true)

  // Extract stable values for dependencies
  const isAdmin = userPermissions?.isAdmin
  const isWriter = userPermissions?.isWriter
  const isReader = userPermissions?.isReader

  // Load landing page links from API
  const loadLandingPageLinks = async () => {
    try {
      setLoading(true)
      const token = await getAccessToken()
      
      if (!token) {
        console.error('No access token available')
        setLandingPageLinks([])
        return
      }
      
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/landing-links/user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setLandingPageLinks(data.items || [])
      } else {
        console.error('Failed to load landing page links:', response.statusText)
        setLandingPageLinks([])
      }
    } catch (error) {
      console.error('Error loading landing page links:', error)
      setLandingPageLinks([])
    } finally {
      setLoading(false)
    }
  }

  // Load links on component mount
  useEffect(() => {
    if (userPermissions) {
      loadLandingPageLinks()
    }
  }, [userPermissions])

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      await logout()
      handleClose()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  // Convert landing page links to applications
  const applications = React.useMemo((): Application[] => {
    console.log('🔍 InternalLandingPage useMemo triggered:', {
      userPermissions,
      landingPageLinks: landingPageLinks.length,
      isAdmin,
      isWriter,
      isReader,
      timestamp: new Date().toISOString()
    })

    const apps: Application[] = []

    // Convert database links to applications
    landingPageLinks
      .filter(link => link.Enabled && link.Visibility === 'visible')
      .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
      .forEach(link => {
        // Parse key features if they exist
        let features: string[] = []
        if (link.KeyFeatures) {
          try {
            features = JSON.parse(link.KeyFeatures)
          } catch (e) {
            console.warn('Failed to parse KeyFeatures for link:', link.Title)
            features = []
          }
        }

        // Get icon from mapping
        const iconComponent = iconMap[link.Icon || 'LinkIcon'] || iconMap['LinkIcon']
        const iconWithStyle = React.cloneElement(iconComponent, {
          sx: { fontSize: 48, color: theme.palette.primary.main }
        })

        // Map database permission groups to frontend permission names
        const mapPermissionGroup = (group: string): string => {
          switch (group.toLowerCase()) {
            case 'sec-fabric-admin':
            case 'sql_admin':
              return 'admin'
            case 'sec-fabric-read-write':
            case 'sql_writers':
              return 'writer'
            case 'sec-fabric-read':
            case 'sql_readers':
              return 'reader'
            case 'all':
              return 'reader'
            default:
              return 'reader'
          }
        }

        // Determine status based on route
        const status = link.Route && link.Route.trim() !== '' ? 'Available' : 'Coming Soon'

        apps.push({
          id: link.Id.toString(),
          title: link.Title,
          description: link.Description || '',
          icon: iconWithStyle,
          status: status,
          requiredPermission: mapPermissionGroup(link.RequiredGroup),
          path: link.Route || null,
          features: features,
          showKeyFeatures: link.ShowKeyFeatures
        })
      })

    // Always add Administration Portal for admin users
    if (isAdmin) {
      console.log('✅ Adding Administration Portal tile')
      apps.push({
        id: 'admin',
        title: 'Administration Portal',
        description: 'Administrative tools for managing users, system configuration, and landing page content.',
        icon: <AdminPanelSettingsIcon sx={{ fontSize: 48, color: theme.palette.error.main }} />,
        status: 'Available',
        requiredPermission: 'admin',
        path: '/admin',
        features: ['User Management', 'Landing Page Configuration', 'System Settings', 'Access Control', 'Audit Logs']
      })
    }

    console.log('🔍 Final applications array:', apps.map((app: Application) => ({ id: app.id, title: app.title, status: app.status })))
    return apps
  }, [theme.palette, landingPageLinks, isAdmin, isWriter, isReader])

  const hasPermission = (requiredPermission: string) => {
    if (!userPermissions) return false
    
    const normalizedPermission = requiredPermission.toLowerCase()
    
    switch (normalizedPermission) {
      case 'admin':
        return userPermissions.isAdmin
      case 'writer':
      case 'writers':
        return userPermissions.isWriter || userPermissions.isAdmin
      case 'reader':
      case 'readers':
        return userPermissions.isReader || userPermissions.isWriter || userPermissions.isAdmin
      default:
        console.warn(`Unknown permission: ${requiredPermission}, defaulting to reader level`)
        return userPermissions.isReader || userPermissions.isWriter || userPermissions.isAdmin
    }
  }

  const getPermissionChip = (requiredPermission: string) => {
    const hasAccess = hasPermission(requiredPermission)
    
    const getPermissionLabel = (permission: string) => {
      const normalizedPermission = permission.toLowerCase()
      switch (normalizedPermission) {
        case 'admin':
          return 'ADMIN'
        case 'writer':
        case 'writers':
          return 'WRITERS'
        case 'reader':
        case 'readers':
          return 'READERS'
        default:
          return 'READERS'
      }
    }
    
    return (
      <Chip
        label={getPermissionLabel(requiredPermission)}
        size="small"
        color={hasAccess ? 'success' : 'default'}
        variant={hasAccess ? 'filled' : 'outlined'}
      />
    )
  }

  const handleApplicationAccess = (app: Application) => {
    if (app.path && hasPermission(app.requiredPermission)) {
      if (app.path.startsWith('http://') || app.path.startsWith('https://')) {
        console.log('🌐 Opening external URL:', app.path)
        window.open(app.path, '_blank', 'noopener,noreferrer')
      } else {
        console.log('🔗 Navigating to internal route:', app.path)
        navigate(app.path)
      }
    }
  }

  // Show loading screen until user permissions are loaded
  if (!userPermissions) {
    return (
      <Box sx={{ 
        flexGrow: 1, 
        minHeight: '100vh', 
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading your applications...
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Box sx={{ flexGrow: 1, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#1a237e' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            FalconMM Client Portal
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Welcome, {user?.name || user?.username || 'User'}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main }}>
                {(user?.name || user?.username || 'U').charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => {
                handleClose()
                // Profile dialog would go here
              }}>
                <AccountCircleIcon sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Welcome Section */}
      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            component="h1"
            gutterBottom
            sx={{ fontWeight: 'bold', color: '#1a237e', textAlign: 'center' }}
          >
            Welcome to FalconMM Client Portal
          </Typography>
          <Typography
            variant="h6"
            component="h2"
            sx={{ textAlign: 'center', color: 'text.secondary', mb: 4 }}
          >
            Access your investment management applications and services
          </Typography>

          {/* User Permissions Display */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Your Access Level:
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              {userPermissions?.isAdmin ? (
                <Chip label="ADMIN" color="error" size="small" />
              ) : userPermissions?.isWriter ? (
                <Chip label="WRITER" color="warning" size="small" />
              ) : userPermissions?.isReader ? (
                <Chip label="READER" color="info" size="small" />
              ) : null}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Applications Grid */}
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '300px',
            textAlign: 'center'
          }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Loading your applications...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait whilst we fetch your personalised dashboard
            </Typography>
          </Box>
        ) : applications.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            minHeight: '300px',
            textAlign: 'center'
          }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No applications available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contact your administrator if you believe this is an error
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {applications.map((app) => {
              const hasAccess = hasPermission(app.requiredPermission)
              const isAvailable = app.status === 'Available'
              
              return (
                <Grid item xs={12} md={6} key={app.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                      '&:hover': hasAccess && isAvailable ? {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[8]
                      } : {},
                      opacity: hasAccess ? 1 : 0.7,
                      border: hasAccess && isAvailable ? `2px solid ${theme.palette.primary.main}` : '1px solid #e0e0e0'
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ mr: 2 }}>
                          {app.icon}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography 
                              variant="h5" 
                              component="h3" 
                              sx={{ 
                                fontWeight: 'bold',
                                ...app.titleStyle
                              }}
                            >
                              {app.title}
                            </Typography>
                            {getPermissionChip(app.requiredPermission)}
                          </Box>
                          <Chip
                            label={app.status}
                            size="small"
                            color={app.status === 'Available' ? 'success' : 'default'}
                            sx={{ mb: 2 }}
                          />
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="body1" 
                        paragraph
                        sx={app.descriptionStyle}
                      >
                        {app.description}
                      </Typography>
                      
                      {/* Key features section removed per user request */}
                    </CardContent>
                    
                    <CardActions sx={{ p: 3, pt: 0 }}>
                      <Button
                        variant={hasAccess && isAvailable ? 'contained' : 'outlined'}
                        color="primary"
                        fullWidth
                        disabled={!hasAccess || !isAvailable}
                        startIcon={isAvailable ? <LaunchIcon /> : <AssessmentIcon />}
                        onClick={() => handleApplicationAccess(app)}
                        sx={{ textTransform: 'none' }}
                      >
                        {!hasAccess 
                          ? 'Access Restricted' 
                          : !isAvailable 
                            ? 'Coming Soon' 
                            : 'Launch Application'
                        }
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        )}
      </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: '#1a237e',
          color: 'white',
          py: 3,
          textAlign: 'center',
          mt: 'auto'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2">
            © 2025 Falcon Money Management LLP. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </>
  )
}

export default InternalLandingPage
